/**
 * GlobalView — Rail Track Renderer
 *
 * Loads Swiss rail network GeoJSON from the API, renders track lines
 * on the Cesium globe, and provides snap-to-track functionality
 * to project train positions onto the nearest rail segment.
 *
 * Features:
 *   - Loads GeoJSON once from /api/v1/rail-network
 *   - Renders tracks as thin colored polylines (clamp-to-ground)
 *   - Spatial grid index for fast nearest-segment lookup
 *   - snapToTrack(lat, lon) returns position on nearest rail track
 */

import * as Cesium from 'cesium';

// ─── Types ───────────────────────────────────────────────────────

interface RailSegment {
  coords: [number, number][]; // [lon, lat] pairs
}

interface GeoJSONFeature {
  type: 'Feature';
  properties: { id: number; name?: string };
  geometry: {
    type: 'LineString';
    coordinates: [number, number][];
  };
}

interface GeoJSONCollection {
  type: 'FeatureCollection';
  features: GeoJSONFeature[];
}

const GRID_CELL_SIZE = 0.05; // ~5 km grid cells in degrees

let segments: RailSegment[] = [];
const spatialGrid = new Map<string, number[]>(); // grid key → segment indices
let loaded = false;

// ─── Spatial Grid ────────────────────────────────────────────────

function gridKey(lat: number, lon: number): string {
  const gx = Math.floor(lon / GRID_CELL_SIZE);
  const gy = Math.floor(lat / GRID_CELL_SIZE);
  return `${gx},${gy}`;
}

function buildSpatialGrid(): void {
  spatialGrid.clear();
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    // Index segment by all grid cells it passes through
    const keys = new Set<string>();
    for (const [lon, lat] of seg.coords) {
      keys.add(gridKey(lat, lon));
    }
    for (const key of keys) {
      const list = spatialGrid.get(key);
      if (list) {
        list.push(i);
      } else {
        spatialGrid.set(key, [i]);
      }
    }
  }
}

// ─── Snap to Track ───────────────────────────────────────────────

/**
 * Find the closest point on any rail track segment to the given position.
 * Returns the snapped position or the original if no nearby track found.
 */
export function snapToTrack(
  lat: number,
  lon: number,
): { lat: number; lon: number } {
  if (!loaded || segments.length === 0) {
    return { lat, lon };
  }

  // Search in surrounding grid cells (3x3 neighborhood)
  const cx = Math.floor(lon / GRID_CELL_SIZE);
  const cy = Math.floor(lat / GRID_CELL_SIZE);

  let bestDist = Infinity;
  let bestLat = lat;
  let bestLon = lon;

  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      const key = `${cx + dx},${cy + dy}`;
      const indices = spatialGrid.get(key);
      if (!indices) continue;

      for (const idx of indices) {
        const seg = segments[idx];
        const result = closestPointOnPolyline(lat, lon, seg.coords);
        if (result.dist < bestDist) {
          bestDist = result.dist;
          bestLat = result.lat;
          bestLon = result.lon;
        }
      }
    }
  }

  // Only snap if within ~2 km (roughly 0.02 degrees)
  if (bestDist < 0.02) {
    return { lat: bestLat, lon: bestLon };
  }

  return { lat, lon };
}

/**
 * Find the closest point on a polyline (array of [lon, lat]) to a given point.
 */
function closestPointOnPolyline(
  lat: number,
  lon: number,
  coords: [number, number][],
): { lat: number; lon: number; dist: number } {
  let bestDist = Infinity;
  let bestLat = lat;
  let bestLon = lon;

  for (let i = 0; i < coords.length - 1; i++) {
    const [lon1, lat1] = coords[i];
    const [lon2, lat2] = coords[i + 1];

    // Project point onto line segment
    const dx = lon2 - lon1;
    const dy = lat2 - lat1;
    const len2 = dx * dx + dy * dy;

    if (len2 === 0) continue;

    let t = ((lon - lon1) * dx + (lat - lat1) * dy) / len2;
    t = Math.max(0, Math.min(1, t));

    const projLon = lon1 + t * dx;
    const projLat = lat1 + t * dy;

    const dist = Math.sqrt(
      (lon - projLon) * (lon - projLon) + (lat - projLat) * (lat - projLat),
    );

    if (dist < bestDist) {
      bestDist = dist;
      bestLat = projLat;
      bestLon = projLon;
    }
  }

  return { lat: bestLat, lon: bestLon, dist: bestDist };
}

// ─── Public API ──────────────────────────────────────────────────

/**
 * Initialize the rail track renderer: load GeoJSON, build spatial index.
 * Tracks are data-only (for snapToTrack) — no GPU rendering.
 */
export async function initRailTrackRenderer(_v?: Cesium.Viewer): Promise<void> {

  try {
    const apiBase = import.meta.env.VITE_API_BASE_URL || '';
    const res = await fetch(`${apiBase}/api/v1/rail-network`);

    if (!res.ok) {
      console.warn(`[RailTrack] Failed to load rail network: HTTP ${res.status}`);
      return;
    }

    const geojson = (await res.json()) as GeoJSONCollection;
    console.log(`[RailTrack] Loaded ${geojson.features.length} track features`);

    // Convert to internal format
    segments = geojson.features
      .filter((f) => f.geometry.type === 'LineString' && f.geometry.coordinates.length >= 2)
      .map((f) => ({ coords: f.geometry.coordinates }));

    // Build spatial index (used by snapToTrack — no GPU rendering)
    buildSpatialGrid();
    console.log(`[RailTrack] Spatial index: ${spatialGrid.size} grid cells (data-only, no rendering)`);

    loaded = true;
  } catch (err) {
    console.error('[RailTrack] Error loading rail network:', err);
  }
}

/** Check if rail tracks are loaded */
export function isRailTrackLoaded(): boolean {
  return loaded;
}

/** Destroy track data and free resources */
export function destroyRailTrackRenderer(): void {
  segments = [];
  spatialGrid.clear();
  loaded = false;
}

